import { RegEdOfficerReviewContentType, useRegEdOfficerReviewLinkResendMutation } from '@Queries/RegEdCompliance';
import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill';
import { styleSheet } from './styles';

export interface IRegEdOfficerReview {
	contentType: RegEdOfficerReviewContentType;
	notificationFailure?: Api.OfficerNotificationFailure;
	/** The API clears the failure once a link goes out, so the host reloads (or clears) its copy. */
	onResent?: () => void;
}

interface IProps extends IRegEdOfficerReview {
	parentId: string;
	impersonationContext?: Api.IImpersonationContext;
	onSendWithoutCompliance?: () => void;
	sendWithoutComplianceLabel?: string;
	isSendingWithoutCompliance?: boolean;
	disabled?: boolean;
	/** Rendered last, so the review history sits under the officer card as it does under every other status. */
	footer?: React.ReactNode;
	styles?: StyleDeclarationValue[];
}

const AWAITING = "This is with your compliance officer for review. It won't send until they file it.";
const NO_COMPLIANCE_EMAIL =
	"No compliance email address is set for this account, so your compliance officer wasn't sent this to review. Ask your administrator to add one, then resend it.";

// The address can be removed after the round's last send, so a missing one is explained whatever the round
// recorded: it's what holds Resend now.
const getExplanation = (
	failure: Api.OfficerNotificationFailure | undefined,
	complianceEmailMissing: boolean
): string => {
	if (complianceEmailMissing) {
		return failure
			? NO_COMPLIANCE_EMAIL
			: `${AWAITING} To resend it, ask your administrator to add a compliance email address.`;
	}
	switch (failure) {
		case Api.OfficerNotificationFailure.SendFailed:
			return "We couldn't reach your compliance officer, so they haven't been sent this to review.";
		case Api.OfficerNotificationFailure.NoComplianceEmail:
			return NO_COMPLIANCE_EMAIL;
		default:
			return AWAITING;
	}
};

export const RegEdOfficerReviewPending: React.FC<IProps> = ({
	parentId,
	contentType,
	notificationFailure,
	onResent,
	impersonationContext,
	onSendWithoutCompliance,
	sendWithoutComplianceLabel,
	isSendingWithoutCompliance,
	disabled,
	footer,
	styles = [],
}) => {
	const userSession = useUserSession();
	const resend = useRegEdOfficerReviewLinkResendMutation({
		onError: error => toast.error(error?.systemMessage || "Couldn't resend the review link. Please try again."),
		onSuccess: () => {
			toast.success('Sent a new review link to your compliance officer.');
			onResent?.();
		},
	});

	// Judged only once preferences have loaded, so a partially loaded account can't hide the way out.
	// Never the admin's own account under impersonation; its compliance address says nothing about this piece.
	const account = impersonationContext ? impersonationContext.account : userSession?.account;
	const complianceEmailMissing =
		!!account?.preferences && !account.preferences.complianceSettings?.emailAddress?.trim();
	const failed = !!notificationFailure;

	return (
		<div className={css(styleSheet.card, ...styles)}>
			<div className={css(styleSheet.title)}>Compliance Review</div>
			<Pill
				variant='label'
				color={failed ? 'red' : 'yellow'}
				label={failed ? 'Not sent to your compliance officer' : 'Awaiting your compliance officer'}
			/>
			<div className={css(styleSheet.explanation)}>{getExplanation(notificationFailure, complianceEmailMissing)}</div>
			<Button
				variant={failed ? 'primary' : 'link'}
				size='sm'
				styles={failed ? [styleSheet.actionButton] : [styleSheet.actionButton, styleSheet.flushLink]}
				// The API refuses without an address, so the button waits for one rather than offering a sure failure.
				disabled={disabled || resend.isPending || complianceEmailMissing}
				isLoading={resend.isPending}
				onClick={() => resend.mutate({ contentType, parentId, impersonationContext })}
			>
				{failed ? 'Resend' : 'Resend to compliance officer'}
			</Button>
			{onSendWithoutCompliance ? (
				<Button
					variant='secondary'
					size='sm'
					disabled={disabled}
					isLoading={isSendingWithoutCompliance}
					styles={[styleSheet.actionButton]}
					onClick={onSendWithoutCompliance}
				>
					{sendWithoutComplianceLabel ?? 'Send Anyway'}
				</Button>
			) : null}
			{footer}
		</div>
	);
};
